import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, BookOpen, Plus, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR")} GNF`;

export default function ComptabiliteDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/dashboard");
      setData(data);
    } catch (e) {
      toast.error("Impossible de charger les données comptables");
    } finally {
      setLoading(false);
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/accounting/seed");
      toast.success(`Plan comptable initialisé : ${data.total_accounts} comptes, ${data.total_journals} journaux`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'initialisation");
    } finally {
      setSeeding(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  const result = data?.month?.resultat || 0;
  const isProfit = result >= 0;

  return (
    <div className="space-y-8" data-testid="comptabilite-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><BookOpen className="h-3 w-3" /><span>COMPTABILITÉ — SYSCOHADA</span></div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#1E3A8A]">Tableau de bord comptable</h1>
          <p className="text-slate-500 mt-1">Vue temps réel de la trésorerie, des produits et des charges.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="comptabilite-refresh"><RefreshCw className="h-4 w-4 mr-2" />Actualiser</Button>
          <Button onClick={seed} disabled={seeding} className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white" data-testid="comptabilite-seed">
            {seeding ? "Initialisation..." : "Initialiser plan comptable"}
          </Button>
          <Link to="/dashboard/comptabilite/saisies">
            <Button className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="comptabilite-new-entry">
              <Plus className="h-4 w-4 mr-2" />Nouvelle saisie
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={Wallet}
          label="Trésorerie totale"
          value={fmt(data?.treasury_total)}
          color="text-[#1E3A8A]"
          bg="bg-blue-50"
          testid="kpi-treasury"
          subtitle="Banques + Caisses"
        />
        <KPICard
          icon={ArrowUpRight}
          label="Produits du mois"
          value={fmt(data?.month?.produits)}
          color="text-[#166534]"
          bg="bg-green-50"
          testid="kpi-month-produits"
          subtitle="Scolarité, ventes, dons"
        />
        <KPICard
          icon={ArrowDownRight}
          label="Charges du mois"
          value={fmt(data?.month?.charges)}
          color="text-[#EA580C]"
          bg="bg-orange-50"
          testid="kpi-month-charges"
          subtitle="Salaires, fournitures..."
        />
        <KPICard
          icon={isProfit ? TrendingUp : TrendingDown}
          label="Résultat du mois"
          value={fmt(result)}
          color={isProfit ? "text-[#166534]" : "text-red-600"}
          bg={isProfit ? "bg-emerald-50" : "bg-red-50"}
          testid="kpi-month-result"
          subtitle={`${data?.month?.entries_count || 0} écritures saisies`}
        />
      </div>

      {/* YTD section */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="ytd-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl text-[#1E3A8A]">Cumul annuel (YTD)</h2>
          <span className="text-xs text-slate-400">Depuis le 1er janvier {new Date().getFullYear()}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-slate-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Total produits</div>
            <div className="text-2xl font-extrabold text-[#166534]">{fmt(data?.ytd?.produits)}</div>
          </div>
          <div className="border border-slate-200 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Total charges</div>
            <div className="text-2xl font-extrabold text-[#EA580C]">{fmt(data?.ytd?.charges)}</div>
          </div>
          <div className="border-2 border-slate-300 rounded-xl p-4 bg-slate-50">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Résultat net annuel</div>
            <div className={`text-2xl font-extrabold ${(data?.ytd?.resultat || 0) >= 0 ? "text-[#166534]" : "text-red-600"}`}>{fmt(data?.ytd?.resultat)}</div>
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <QuickLink to="/dashboard/comptabilite/saisies" title="Saisies comptables" desc="Enregistrer recettes, dépenses, salaires." color="bg-[#166534]" />
        <QuickLink to="/dashboard/comptabilite/journaux" title="Journaux" desc="Caisse, Banque, Ventes, Achats, OD." color="bg-[#1E3A8A]" />
        <QuickLink to="/dashboard/comptabilite/balance" title="Balance générale" desc="Soldes de tous les comptes." color="bg-[#EA580C]" />
        <QuickLink to="/dashboard/comptabilite/exploitation" title="Compte d'exploitation" desc="Produits − Charges = Résultat (P&L)." color="bg-purple-600" />
        <QuickLink to="/dashboard/comptabilite/tresorerie" title="Trésorerie" desc="Soldes Banques & Caisses temps réel." color="bg-cyan-600" />
        <QuickLink to="/dashboard/comptabilite/plan-comptable" title="Plan comptable" desc="Comptes & paramétrage SYSCOHADA." color="bg-slate-700" />
      </div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color, bg, testid, subtitle }) {
  return (
    <div className={`${bg} rounded-2xl border border-white p-5 shadow-sm`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</span>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div className={`font-heading font-extrabold text-2xl ${color} tabular-nums`}>{value}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function QuickLink({ to, title, desc, color }) {
  return (
    <Link to={to} className="group block bg-white border border-slate-200 hover:border-[#1E3A8A] rounded-2xl p-5 transition-all hover:shadow-lg" data-testid={`quicklink-${to.split('/').pop()}`}>
      <div className={`w-10 h-10 ${color} rounded-xl mb-3 group-hover:scale-110 transition-transform`} />
      <div className="font-heading font-bold text-lg text-[#1E3A8A] mb-1">{title}</div>
      <div className="text-sm text-slate-500">{desc}</div>
    </Link>
  );
}
